import { ClientError, GraphQLClient, type RequestDocument } from "graphql-request";
import { isSentinelValue } from "@/lib/env";

// Get the GraphQL endpoint URL - use absolute URL for graphql-request compatibility
// TODO: Added _ to pass lint checks. If this is not needed, remove it.
const _getGraphqlUrl = (): string => {
  // In the browser, use the current origin (works with Next.js rewrites)
  if (typeof window !== "undefined") {
    return `${window.location.origin}/api/graphql`;
  }
  // SSR fallback (should rarely be used since queries are client-side)
  return "/api/graphql";
};

const resolveGraphqlEndpoint = () => {
  if (typeof window !== "undefined") {
    return new URL("/api/graphql", window.location.origin).toString();
  }

  const graphqlEndpoint = process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT;
  if (graphqlEndpoint && !isSentinelValue(graphqlEndpoint)) {
    return graphqlEndpoint;
  }

  const apiBase = process.env.NEXT_PUBLIC_API_BASE_URL;
  if (apiBase && !isSentinelValue(apiBase)) {
    return new URL("/api/graphql", apiBase).toString();
  }

  return "http://localhost:8080/api/graphql";
};

const graphqlClient = new GraphQLClient(resolveGraphqlEndpoint(), {
  credentials: "include",
});

const refreshAccessToken = async () => {
  // WorkOS session refresh is handled server-side in auth middleware.
  // Revalidate via an authenticated endpoint so cookies can be rotated
  // before retrying the original GraphQL request.
  try {
    const res = await fetch("/api/auth/status", {
      method: "GET",
      credentials: "include",
      headers: {
        Accept: "application/json",
      },
      signal: AbortSignal.timeout(20_000),
    });

    return res.ok;
  } catch {
    return false;
  }
};

const isUnauthorizedError = (error: unknown) => {
  if (error instanceof ClientError) {
    return error.response.status === 401;
  }

  if (!error || typeof error !== "object") {
    return false;
  }

  if (!("response" in error)) {
    return false;
  }

  const response = (error as { response?: unknown }).response;
  if (!response || typeof response !== "object") {
    return false;
  }

  return (response as { status?: unknown }).status === 401;
};

export const graphqlErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof ClientError) {
    const firstError = error.response?.errors?.[0];
    if (firstError) {
      // Prefer specific validation field messages over Lighthouse's generic wrapper.
      // e.g. extensions.validation = { user: ["The inviter account does not exist…"] }
      const validation = (firstError as { extensions?: { validation?: Record<string, string[]> } }).extensions
        ?.validation;
      if (validation) {
        const messages = Object.values(validation).flat().filter(Boolean);
        if (messages.length > 0) return messages.join(" ");
      }
      if (firstError.message) return firstError.message;
    }
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

export const graphqlRequest = async <TData, TVariables extends Record<string, unknown> = Record<string, never>>(
  document: RequestDocument,
  variables?: TVariables,
): Promise<TData> => {
  const emptyVariables: Record<string, unknown> = {};
  const executeRequest = (vars: Record<string, unknown>) => {
    return graphqlClient.request<TData, Record<string, unknown>>(document, vars);
  };

  try {
    if (variables === undefined) {
      return await executeRequest(emptyVariables);
    }

    return await executeRequest(variables);
  } catch (error) {
    if (!isUnauthorizedError(error)) {
      throw error;
    }

    const refreshed = await refreshAccessToken();
    if (!refreshed) {
      throw error;
    }

    if (variables === undefined) {
      return await executeRequest(emptyVariables);
    }

    return await executeRequest(variables);
  }
};
